(() => {
  if (window.KaizenProductShelf) return;

  const CARD_SELECTOR = '.product-list--editorial-shelf [data-product-shelf-card][data-color-source="auto"]';
  const IMAGE_SELECTOR = '.card-gallery img';

  /**
   * Pulls a representative background color from the clean upper-side areas of
   * a product photo. These pixels avoid both the centered product and the darker
   * tabletop shadow that often sits along the bottom edge.
   *
   * @param {HTMLImageElement} image
   * @returns {{ red: number, green: number, blue: number } | null}
   */
  function sampleEdgeColor(image) {
    const size = 48;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    if (!context || !image.naturalWidth || !image.naturalHeight) return null;

    canvas.width = size;
    canvas.height = size;

    try {
      context.drawImage(image, 0, 0, size, size);
      const pixels = context.getImageData(0, 0, size, size).data;
      const channels = { red: [], green: [], blue: [] };
      const minimumX = 0.08;
      const maximumX = 0.92;
      const innerLeft = 0.28;
      const innerRight = 0.72;
      const minimumY = 0.06;
      const maximumY = 0.62;

      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          const normalizedX = x / (size - 1);
          const normalizedY = y / (size - 1);
          const isUpperSideBackground =
            normalizedY >= minimumY &&
            normalizedY <= maximumY &&
            normalizedX >= minimumX &&
            normalizedX <= maximumX &&
            (normalizedX <= innerLeft || normalizedX >= innerRight);
          if (!isUpperSideBackground) continue;

          const offset = (y * size + x) * 4;
          if (pixels[offset + 3] < 220) continue;

          channels.red.push(pixels[offset]);
          channels.green.push(pixels[offset + 1]);
          channels.blue.push(pixels[offset + 2]);
        }
      }

      if (!channels.red.length) return null;

      const representativeBackdrop = (values) => {
        values.sort((a, b) => a - b);
        return values[Math.floor((values.length - 1) * 0.94)];
      };

      return {
        red: representativeBackdrop(channels.red),
        green: representativeBackdrop(channels.green),
        blue: representativeBackdrop(channels.blue),
      };
    } catch (_error) {
      return null;
    }
  }

  /** @param {{ red: number, green: number, blue: number }} color */
  function readableTextColor(color) {
    const linearize = (channel) => {
      const value = channel / 255;
      return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    };

    const luminance =
      0.2126 * linearize(color.red) + 0.7152 * linearize(color.green) + 0.0722 * linearize(color.blue);

    return luminance > 0.42 ? '#171715' : '#f8f6f0';
  }

  /** @param {HTMLElement} card */
  function matchCard(card) {
    const image = card.querySelector(IMAGE_SELECTOR);
    if (!(image instanceof HTMLImageElement)) return;

    const applyColor = () => {
      const color = sampleEdgeColor(image);
      if (!color) return;

      const rgb = `rgb(${color.red} ${color.green} ${color.blue})`;
      card.style.setProperty('--shelf-card-background', rgb);
      card.style.setProperty('--shelf-card-text', readableTextColor(color));
      card.dataset.colorMatched = 'true';
    };

    if (image.complete && image.naturalWidth) {
      applyColor();
    } else {
      image.addEventListener('load', applyColor, { once: true });
    }
  }

  /** @param {ParentNode} [root] */
  function matchAll(root = document) {
    root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (card instanceof HTMLElement && card.dataset.colorMatched !== 'true') matchCard(card);
    });
  }

  window.KaizenProductShelf = { matchAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => matchAll(), { once: true });
  } else {
    matchAll();
  }

  document.addEventListener('shopify:section:load', (event) => matchAll(event.target));
})();
